import Button from 'components/Button';
import React, { useEffect, useState } from 'react';
import { Form, Modal } from 'react-bootstrap';
import { useCustomerService } from 'services/customers';
import { Customer } from 'types/customer';
import {
  formatTinForInput,
  TIN_PLACEHOLDER,
  validateTin,
} from 'utils/tinUtils';
import styles from './Customers.module.scss';

interface AddCustomerModalProps {
  show: boolean;
  onHide: () => void;
  onSuccess: (customer: Customer) => void;
  onError: (message: string) => void;
  initialName?: string;
}

function AddCustomerModal({
  show,
  onHide,
  onSuccess,
  onError,
  initialName = '',
}: AddCustomerModalProps) {
  const customerService = useCustomerService();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [tinNumber, setTinNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tinError, setTinError] = useState<string | null>(null);

  useEffect(() => {
    if (show) {
      // Set initial name when modal opens
      setName(initialName);
      setEmail('');
      setPhone('');
      setAddress('');
      setTinNumber('');
      setNotes('');
    } else {
      // Clear all fields when modal closes
      setName('');
      setEmail('');
      setPhone('');
      setAddress('');
      setTinNumber('');
      setNotes('');
    }
  }, [show, initialName]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (!name.trim()) {
      onError('Customer name is required');
      return;
    }

    const tinValidationError = validateTin(tinNumber);
    if (tinValidationError) {
      setTinError(tinValidationError);
      return;
    }
    setTinError(null);

    setIsSubmitting(true);
    try {
      const sanitizedTin = tinNumber.replace(/\D/g, '');
      const createdCustomer = await customerService.createCustomer({
        name: name.trim(),
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        address: address.trim() || undefined,
        tinNumber: sanitizedTin || undefined,
        notes: notes.trim() || undefined,
      });
      onSuccess(createdCustomer);
      onHide();
    } catch (error) {
      onError(
        error instanceof Error ? error.message : 'Failed to create customer',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      className={styles.modal}
      contentClassName={styles.modalContent}
      dialogClassName={styles.modalDialog}
      backdrop="static"
    >
      <Modal.Header closeButton className={styles.modalHeader}>
        <Modal.Title className={styles.modalTitle}>Add Customer</Modal.Title>
      </Modal.Header>
      <Modal.Body className={styles.modalBody}>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            void handleSubmit(e);
          }}
        >
          <Form.Group controlId="customerName" className={styles.formGroup}>
            <Form.Label className={styles.label}>Name *</Form.Label>
            <Form.Control
              type="text"
              placeholder="Customer name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className={styles.input}
            />
          </Form.Group>

          <Form.Group controlId="customerEmail" className={styles.formGroup}>
            <Form.Label className={styles.label}>Email</Form.Label>
            <Form.Control
              type="email"
              placeholder="customer@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={styles.input}
            />
          </Form.Group>

          <Form.Group controlId="customerPhone" className={styles.formGroup}>
            <Form.Label className={styles.label}>Phone</Form.Label>
            <Form.Control
              type="text"
              placeholder="+1234567890"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className={styles.input}
            />
          </Form.Group>

          <Form.Group controlId="customerAddress" className={styles.formGroup}>
            <Form.Label className={styles.label}>Address</Form.Label>
            <Form.Control
              type="text"
              placeholder="123 Main St, City, State 12345"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              className={styles.input}
            />
          </Form.Group>

          <Form.Group
            controlId="customerTinNumber"
            className={styles.formGroup}
          >
            <Form.Label className={styles.label}>TIN Number</Form.Label>
            <Form.Control
              type="text"
              placeholder={TIN_PLACEHOLDER}
              value={tinNumber}
              onChange={(e) => {
                setTinNumber(e.target.value);
                if (tinError) setTinError(null);
              }}
              onBlur={() => {
                const error = validateTin(tinNumber);
                if (error) {
                  setTinError(error);
                  return;
                }
                const formatted = formatTinForInput(tinNumber);
                setTinNumber(formatted);
              }}
              className={styles.input}
            />
            {tinError && (
              <Form.Text className={styles.errorText}>{tinError}</Form.Text>
            )}
          </Form.Group>

          <Form.Group controlId="customerNotes" className={styles.formGroup}>
            <Form.Label className={styles.label}>Notes</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Additional notes about the customer..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className={styles.textarea}
            />
          </Form.Group>

          <div className={styles.modalFooter}>
            <Button
              type="button"
              variant="outline"
              onClick={onHide}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Create Customer'}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}

export default AddCustomerModal;
